import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import ConfirmTypeDialog from "@/components/ConfirmTypeDialog";
import { toast } from "sonner";
import { Building2, Mail, Upload, Copy, Users2, Package, Pencil, Search, Trash2, FileCheck2, ClipboardList } from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const canManage = ["admin","approver","super_admin"].includes(user?.role);

  const [customers, setCustomers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", company: "", primary_color: "#01ADEF", logo_url: "" });
  const [inviteResult, setInviteResult] = useState(null);

  const [assigning, setAssigning] = useState(null);
  const [assignForm, setAssignForm] = useState({ organization_id: "", sub_organization_id: "", assigned_inspector_id: "" });

  const [deleting, setDeleting] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importOpts, setImportOpts] = useState({ create_missing_orgs: true, dry_run: true });
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o, u] = await Promise.all([
        api.get("/customers"),
        api.get("/organizations").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setCustomers(c.data);
      setOrgs(o.data);
      setUsers(u.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  const inspectors = useMemo(() => users.filter(u => ["inspector", "admin"].includes(u.role)), [users]);
  const rootOrgs = useMemo(() => orgs.filter(o => !o.parent_id), [orgs]);
  const subOrgs = useMemo(() => orgs.filter(o => o.parent_id === assignForm.organization_id), [orgs, assignForm.organization_id]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter(c =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term) ||
      (c.organization_name || "").toLowerCase().includes(term) ||
      (c.inspector_name || "").toLowerCase().includes(term));
  }, [customers, q]);

  if (user && !canManage) return <Navigate to="/dashboard" replace />;

  const sendInvite = async () => {
    try {
      const brand = { company: invite.company || undefined, primary_color: invite.primary_color || undefined, logo_url: invite.logo_url || undefined };
      const { data } = await api.post("/invites", { email: invite.email, name: invite.name, brand });
      setInviteResult(data);
      toast.success("Invitation created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAssign = (c) => {
    setAssignForm({
      organization_id: c.organization_id || "",
      sub_organization_id: c.sub_organization_id || "",
      assigned_inspector_id: c.assigned_inspector_id || "",
    });
    setAssigning(c);
  };

  const saveAssign = async () => {
    try {
      await api.patch(`/customers/${assigning.id}`, {
        organization_id: assignForm.organization_id || null,
        sub_organization_id: assignForm.sub_organization_id || null,
        assigned_inspector_id: assignForm.assigned_inspector_id || null,
      });
      toast.success("Customer updated");
      setAssigning(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/customers/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "customers-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const runImport = async () => {
    if (!importFile) return toast.error("Choose a CSV file");
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const qp = new URLSearchParams({ create_missing_orgs: importOpts.create_missing_orgs, dry_run: importOpts.dry_run }).toString();
      const { data } = await api.post(`/customers/import?${qp}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (!importOpts.dry_run) { toast.success(`Import complete — ${data.created} created, ${data.updated} updated`); load(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const copyInvite = () => {
    if (inviteResult?.invite_url) {
      navigator.clipboard.writeText(inviteResult.invite_url);
      toast.success("Link copied");
    }
  };

  const openDetail = async (c) => {
    setDetail({ customer: c, equipment: [], certificates: [], open_jobs: [] });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/customers/${c.id}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customer");
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      const { data } = await api.delete(`/customers/${deleting.id}`);
      const n = data?.equipment_unlinked || 0;
      toast.success(n ? `Customer deleted — ${n} equipment unlinked` : "Customer deleted");
      setDeleting(null);
      if (detail?.customer?.id === deleting.id) setDetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="customers-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Directory</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Customers</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">Every customer with a portal login. Assign an inspector, link them to an organisation, or invite a new one.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button data-testid="import-customers-btn" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} variant="outline" className="rounded-full border-gray-300"><Upload className="w-4 h-4 mr-2" />Import CSV</Button>
          )}
          {user?.role !== "approver" && (
            <Button data-testid="invite-customer-btn" onClick={() => { setInviteResult(null); setInvite({ email: "", name: "", company: "", primary_color: "#01ADEF", logo_url: "" }); setInviteOpen(true); }} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Mail className="w-4 h-4 mr-2" />Invite Customer</Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <Kpi label="Total Customers" value={customers.length} icon={Users2} tint="#01ADEF" testid="kpi-total-customers" />
        <Kpi label="With Equipment" value={customers.filter(c => (c.asset_count || 0) > 0).length} icon={Package} tint="#059669" testid="kpi-with-equipment" />
        <Kpi label="Without Inspector" value={customers.filter(c => !c.assigned_inspector_id).length} icon={Building2} tint="#DC2626" testid="kpi-no-inspector" />
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-200 p-3 flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="customer-search" placeholder="Search name, email, org or inspector…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 rounded-md" />
        </div>
        <span className="text-xs text-gray-500 font-mono">{filtered.length} / {customers.length}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-200 bg-gray-50">
            <Th>Customer</Th><Th>Organisation</Th><Th>Assigned Inspector</Th><Th className="text-right">Equipment</Th><Th></Th>
          </tr></thead>
          <tbody data-testid="customers-table">
            {loading ? (
              <tr><td colSpan={5} className="text-center py-10 text-gray-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-gray-500">{customers.length === 0 ? "No customers yet — invite the first one." : "No customers match this search."}</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`customer-row-${c.id}`} onClick={() => openDetail(c)}>
                <Td>
                  <div className="font-semibold">{c.name || c.email}</div>
                  {c.name && <div className="text-xs text-gray-500 font-mono">{c.email}</div>}
                </Td>
                <Td>
                  {c.organization_name ? (
                    <div>
                      <div className="text-sm font-semibold">{c.organization_name}</div>
                      {c.sub_organization_name && <div className="text-[10px] uppercase tracking-wider text-gray-500">{c.sub_organization_name}</div>}
                    </div>
                  ) : <span className="text-gray-400 text-xs">—</span>}
                </Td>
                <Td>
                  {c.inspector_name ? (
                    <div>
                      <div className="text-sm font-semibold">{c.inspector_name}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{c.inspector_email}</div>
                    </div>
                  ) : <span className="text-gray-400 text-xs">Unassigned</span>}
                </Td>
                <Td className="text-right font-mono font-semibold">{c.asset_count || 0}</Td>
                <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                  {user?.role === "admin" && (
                    <div className="flex items-center justify-end gap-3">
                      <button data-testid={`edit-customer-${c.id}`} onClick={() => openAssign(c)} title="Edit assignment" className="text-gray-400 hover:text-[#01ADEF]"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`delete-customer-${c.id}`} onClick={() => setDeleting(c)} title="Delete customer" className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="invite-customer-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Mail className="w-5 h-5 text-[#01ADEF]" />Invite Customer</DialogTitle></DialogHeader>
          {inviteResult ? (
            <div className="space-y-3 py-2 text-sm">
              <div className="bg-[#D1FAE5] border border-[#6EE7B7] text-[#065F46] rounded px-3 py-2">
                {inviteResult.email_sent ? "Invitation email sent." : "Email not configured — share this link manually with your customer:"}
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-xs break-all">{inviteResult.invite_url}</div>
              <Button variant="outline" size="sm" onClick={copyInvite} className="rounded-full"><Copy className="w-3 h-3 mr-1" />Copy link</Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <Field label="Customer Email *"><Input data-testid="invite-email" type="email" value={invite.email} onChange={e => setInvite({ ...invite, email: e.target.value })} className="rounded-md" placeholder="customer@example.com" /></Field>
              <Field label="Customer Name"><Input data-testid="invite-name" value={invite.name} onChange={e => setInvite({ ...invite, name: e.target.value })} className="rounded-md" /></Field>
              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2">Portal Branding (optional)</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Company"><Input value={invite.company} onChange={e => setInvite({ ...invite, company: e.target.value })} className="rounded-md" /></Field>
                  <Field label="Brand Colour"><Input type="color" value={invite.primary_color} onChange={e => setInvite({ ...invite, primary_color: e.target.value })} className="rounded-md h-9" /></Field>
                </div>
                <Field label="Logo URL"><Input value={invite.logo_url} onChange={e => setInvite({ ...invite, logo_url: e.target.value })} placeholder="https://" className="rounded-md" /></Field>
              </div>
            </div>
          )}
          <DialogFooter>
            {inviteResult ? (
              <Button onClick={() => setInviteOpen(false)} className="bg-[#01ADEF] hover:bg-[#0198D6]">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button data-testid="send-invite-btn" onClick={sendInvite} disabled={!invite.email} className="bg-[#01ADEF] hover:bg-[#0198D6]">Send Invite</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="rounded-md" data-testid="edit-customer-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit — {assigning?.name || assigning?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Organisation">
              <Select value={assignForm.organization_id || "none"} onValueChange={v => setAssignForm({ ...assignForm, organization_id: v === "none" ? "" : v, sub_organization_id: "" })}>
                <SelectTrigger className="rounded-md" data-testid="assign-org"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {rootOrgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sub-organisation">
              <Select value={assignForm.sub_organization_id || "none"} onValueChange={v => setAssignForm({ ...assignForm, sub_organization_id: v === "none" ? "" : v })} disabled={!assignForm.organization_id}>
                <SelectTrigger className="rounded-md" data-testid="assign-sub-org"><SelectValue placeholder={assignForm.organization_id ? "— None —" : "Pick an organisation first"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {subOrgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Assigned Inspector">
              <Select value={assignForm.assigned_inspector_id || "none"} onValueChange={v => setAssignForm({ ...assignForm, assigned_inspector_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-md" data-testid="assign-inspector"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {inspectors.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssigning(null)}>Cancel</Button>
            <Button data-testid="save-customer-btn" onClick={saveAssign} className="bg-[#01ADEF] hover:bg-[#0198D6]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="customer-detail-drawer">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">{detail?.customer?.name || detail?.customer?.email}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{detail?.customer?.email}</SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {detail?.customer?.organization_name && <Badge variant="outline" className="rounded-full"><Building2 className="w-3 h-3 mr-1" />{detail.customer.organization_name}</Badge>}
            {detail?.customer?.inspector_name && <Badge variant="outline" className="rounded-full">Inspector: {detail.customer.inspector_name}</Badge>}
          </div>

          {detailLoading ? (
            <div className="py-10 text-center text-gray-500 text-sm">Loading…</div>
          ) : (
            <div className="mt-6 space-y-6">
              <DetailSection icon={Package} title="Equipment" count={detail?.equipment?.length || 0} testid="detail-equipment">
                {(detail?.equipment || []).length === 0 ? <Empty text="No equipment linked" /> : detail.equipment.map(e => (
                  <div key={e.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-testid={`detail-equipment-${e.id}`}>
                    <div>
                      <div className="text-sm font-semibold">{e.name || "Untitled"}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{e.asset_tag || "—"}</div>
                    </div>
                    <StatusPill value={e.status} />
                  </div>
                ))}
              </DetailSection>

              <DetailSection icon={FileCheck2} title="Certificates" count={detail?.certificates?.length || 0} testid="detail-certificates">
                {(detail?.certificates || []).length === 0 ? <Empty text="No certificates" /> : detail.certificates.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-testid={`detail-certificate-${c.id}`}>
                    <div>
                      <div className="text-sm font-semibold font-mono">{c.certificate_number}</div>
                      <div className="text-[10px] text-gray-500">{c.asset_name} · exp {(c.expiry_date || "").slice(0, 10)}</div>
                    </div>
                    <StatusPill value={c.approval_status} />
                  </div>
                ))}
              </DetailSection>

              <DetailSection icon={ClipboardList} title="Open Jobs" count={detail?.open_jobs?.length || 0} testid="detail-open-jobs">
                {(detail?.open_jobs || []).length === 0 ? <Empty text="No open jobs" /> : detail.open_jobs.map(j => (
                  <div key={j.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-testid={`detail-job-${j.id}`}>
                    <div>
                      <div className="text-sm font-semibold">{j.asset_name}</div>
                      <div className="text-[10px] text-gray-500">{j.inspection_type} · {(j.scheduled_date || "").slice(0, 10)}</div>
                    </div>
                    <StatusPill value={j.status} />
                  </div>
                ))}
              </DetailSection>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <ConfirmTypeDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        phrase={deleting?.name || deleting?.email}
        title="Delete customer?"
        description="This removes the customer's portal login. Their equipment is kept but the customer link is cleared. This cannot be undone."
        confirmLabel="Delete customer"
        onConfirm={confirmDelete}
        testidPrefix="delete-customer-confirm"
      />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-md max-w-2xl" data-testid="import-customers-dialog">
          <DialogHeader><DialogTitle className="font-display">Bulk Import Customers</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-xs text-gray-600">Upload a CSV with columns: email, name, organization, sub_organization, inspector_email. Download the template to see the exact format.</p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} className="rounded-full"><Upload className="w-3 h-3 mr-1 rotate-180" />Download template</Button>
            <Input type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0])} data-testid="import-file" />
            <div className="flex items-center gap-4 text-xs">
              <label className="flex items-center gap-1"><input type="checkbox" checked={importOpts.create_missing_orgs} onChange={e => setImportOpts({ ...importOpts, create_missing_orgs: e.target.checked })} /> Auto-create missing organisations</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={importOpts.dry_run} onChange={e => setImportOpts({ ...importOpts, dry_run: e.target.checked })} data-testid="import-dry-run" /> Validate only (dry run)</label>
            </div>
            {importResult && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 font-mono text-xs">
                Total: {importResult.total} · Created: {importResult.created} · Updated: {importResult.updated} · Errors: {importResult.errors?.length || 0}
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto">
                    {importResult.errors.map((e, i) => <div key={`err-${i}-${e.row}`} className="text-[#B91C1C]">Row {e.row}: {e.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button data-testid="run-import-btn" onClick={runImport} disabled={importing || !importFile} className="bg-[#01ADEF] hover:bg-[#0198D6]">{importing ? "Processing…" : (importOpts.dry_run ? "Validate CSV" : "Import Customers")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
const Th = ({ children, className = "" }) => <th className={`text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Empty = ({ text }) => <div className="text-xs text-gray-400 py-3">{text}</div>;
const DetailSection = ({ icon: Icon, title, count, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#01ADEF]" />
      <span className="text-xs uppercase tracking-wider font-semibold text-gray-700">{title}</span>
      <span className="text-[10px] font-mono text-gray-400">({count})</span>
    </div>
    <div className="bg-white border border-gray-200 rounded-md px-3">{children}</div>
  </div>
);
const StatusPill = ({ value }) => {
  const v = (value || "—").toLowerCase();
  const map = {
    approved: "bg-[#D1FAE5] text-[#065F46]", pending: "bg-[#FEF3C7] text-[#92400E]", rejected: "bg-[#FEE2E2] text-[#991B1B]",
    active: "bg-[#D1FAE5] text-[#065F46]", scheduled: "bg-[#DBEAFE] text-[#1E40AF]", in_progress: "bg-[#FEF3C7] text-[#92400E]",
    out_of_service: "bg-[#FEE2E2] text-[#991B1B]", retired: "bg-gray-100 text-gray-500",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${map[v] || "bg-gray-100 text-gray-500"}`}>{(value || "—").replace(/_/g, " ")}</span>;
};
const Kpi = ({ label, value, icon: Icon, tint, testid }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: tint }}><Icon className="w-3.5 h-3.5 text-white" strokeWidth={2.5} /></div>
    </div>
    <div className="font-display text-3xl font-bold tracking-tight" style={{ color: "#FE5000" }}>{value}</div>
  </div>
);
